/**
 * Locale registry. Each entry maps a `code` (the BCP-47 tag we ship JSON
 * files under in `src/i18n/locales/`) to the language's native name (what
 * the user sees in the picker) and English name (for analytics + dev tools).
 *
 * The `code` here MUST match the filename in `src/i18n/locales/{code}.json`.
 *
 * Languages chosen by fitness-app market priority (Western markets + key
 * Asian markets), confirmed with the user 2026-05-01. RTL languages
 * (Arabic) are translated but rendered LTR for now - we can add a layout
 * flip later if needed.
 */
export interface AppLocale {
  code: string;
  nativeName: string;
  englishName: string;
  /** True for languages that are normally written right-to-left. We currently
   *  keep LTR layout but expose this so future code can flip if desired. */
  rtl?: boolean;
}

export const APP_LOCALES: AppLocale[] = [
  { code: 'en', nativeName: 'English', englishName: 'English' },
  { code: 'es', nativeName: 'Español', englishName: 'Spanish' },
  { code: 'fr', nativeName: 'Français', englishName: 'French' },
  { code: 'de', nativeName: 'Deutsch', englishName: 'German' },
  { code: 'it', nativeName: 'Italiano', englishName: 'Italian' },
  { code: 'pt-BR', nativeName: 'Português (Brasil)', englishName: 'Portuguese (Brazil)' },
  { code: 'nl', nativeName: 'Nederlands', englishName: 'Dutch' },
  { code: 'pl', nativeName: 'Polski', englishName: 'Polish' },
  { code: 'sv', nativeName: 'Svenska', englishName: 'Swedish' },
  { code: 'no', nativeName: 'Norsk', englishName: 'Norwegian' },
  { code: 'da', nativeName: 'Dansk', englishName: 'Danish' },
  { code: 'fi', nativeName: 'Suomi', englishName: 'Finnish' },
  { code: 'ru', nativeName: 'Русский', englishName: 'Russian' },
  { code: 'tr', nativeName: 'Türkçe', englishName: 'Turkish' },
  { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', rtl: true },
  { code: 'zh-CN', nativeName: '中文 (简体)', englishName: 'Chinese (Simplified)' },
  { code: 'ja', nativeName: '日本語', englishName: 'Japanese' },
  { code: 'ko', nativeName: '한국어', englishName: 'Korean' },
  { code: 'hi', nativeName: 'हिन्दी', englishName: 'Hindi' },
  { code: 'id', nativeName: 'Bahasa Indonesia', englishName: 'Indonesian' },
];

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALE_CODES = APP_LOCALES.map((l) => l.code);

/**
 * Resolve a raw locale string (e.g. from `Device.getLanguageCode()` or
 * `navigator.language`) to one of our supported codes. Falls back to English
 * if no match is found.
 *
 * Matching rules (in order):
 *  1. Exact match (e.g. "pt-BR" -> "pt-BR")
 *  2. Language prefix match (e.g. "pt-PT" -> "pt-BR" because that's what we ship)
 *  3. Default to "en"
 */
export function resolveSupportedLocale(input: string | null | undefined): string {
  if (!input) return DEFAULT_LOCALE;
  const normalised = input.trim();
  if (!normalised) return DEFAULT_LOCALE;

  // Try exact match first.
  if (SUPPORTED_LOCALE_CODES.includes(normalised)) return normalised;

  // Try lowercased exact match (e.g. "PT-br" -> "pt-BR" via case-insensitive cmp).
  const lower = normalised.toLowerCase();
  const ciMatch = SUPPORTED_LOCALE_CODES.find((c) => c.toLowerCase() === lower);
  if (ciMatch) return ciMatch;

  // Try language-only prefix match.
  const lang = lower.split(/[-_]/)[0];
  const prefixMatch = SUPPORTED_LOCALE_CODES.find(
    (c) => c.toLowerCase().split(/[-_]/)[0] === lang
  );
  if (prefixMatch) return prefixMatch;

  return DEFAULT_LOCALE;
}
